/* Входы формы «Слайдов с преимуществами» ⇄ состояние движка шаблона (план вехи 3, шаг 2; mcard-infog K-IN). Чистые функции без DOM:
   viewOf — что показать в S по входам сервиса (повтор из «Истории», «Изменить», заявка родной формы), inputsOf — входы из S (зеркало
   inputs-mirror, точки записи — syncFoot движка). Ключи и кодирование — те же, что пишет родная форма (SlideOrderForm.tsx): одно поле
   «Название и преимущества» (text) или пара name + advantages, specs строками «ключ: значение», clean_cover значением формы (false|true
   или text|clean), количество слайдов строкой, оформление и площадка — только опубликованные значения. Слоты фото пишет мост загрузок
   (media-bridge): inputsOf их не трогает. */
import type { StudioForm } from '../studio/studio-api';
import {
  enumOf,
  formatSpecs,
  joinProductText,
  parseSpecs,
  photoSlots,
  splitProductText,
  textOf,
  type Field,
  type SpecRow,
} from '../slides/product-model';

/** Поля формы экрана (как SlideOrderForm: одно поле text, без него — пара name + advantages) */
export interface InfogFields {
  text?: Field;
  name?: Field;
  advantages?: Field;
  style?: Field;
  platform?: Field;
  slides?: Field;
  cover?: Field;
  specs?: Field;
  photos: Field[];
}
export function infogFields(form: StudioForm | null | undefined): InfogFields {
  const text = textOf(form, 'text');
  return {
    text,
    name: text ? undefined : textOf(form, 'name'),
    advantages: text ? undefined : textOf(form, 'advantages'),
    style: enumOf(form, 'style'),
    platform: enumOf(form, 'platform'),
    slides: enumOf(form, 'slides'),
    cover: enumOf(form, 'cover'),
    specs: textOf(form, 'specs'),
    photos: form ? photoSlots(form) : [],
  };
}
/** Значение «Без надписей» и «С надписями» поля обложки */
export const cleanValue = (values: readonly string[]) => values.find(value => value === 'true' || value === 'clean');
export const textCoverValue = (values: readonly string[]) => values.find(value => value === 'false' || value === 'text');

/** Что экран держит в S: поле «Название и преимущества» как есть, оформление, площадка, слайдов, «Без надписей», характеристики */
export interface InfogView {
  text: string;
  sty?: string;
  mp?: string;
  n?: number;
  clean?: boolean;
  specs: SpecRow[];
}

/** Входы → вид S (только опубликованные поля; неопубликованное не задано) */
export function viewOf(form: StudioForm | null | undefined, inputs: Record<string, string>): InfogView {
  const f = infogFields(form);
  const text = f.text
    ? (inputs[f.text.key] ?? '')
    : f.name
      ? joinProductText(inputs[f.name.key] ?? '', (f.advantages ? (inputs[f.advantages.key] ?? '') : '').split('\n'))
      : '';
  const view: InfogView = { text, specs: f.specs ? parseSpecs(inputs[f.specs.key]) : [] };
  const pick = (field: Field | undefined) => {
    const value = field ? inputs[field.key] : undefined;
    return value !== undefined && field!.values.includes(value) ? value : undefined;
  };
  const sty = f.style ? inputs[f.style.key] : undefined;
  if (sty) view.sty = sty;
  const mp = pick(f.platform);
  if (mp) view.mp = mp;
  const n = pick(f.slides);
  if (n && Number.isSafeInteger(Number(n))) view.n = Number(n);
  const cover = pick(f.cover);
  if (cover !== undefined) view.clean = cover === cleanValue(f.cover!.values);
  return view;
}

/** Характеристики для входа: строки с ключом или значением (как их читает parseSpecs) */
const specRows = (rows: readonly (readonly string[])[]): SpecRow[] =>
  rows.map(row => [String(row[0] ?? '').trim(), String(row[1] ?? '').trim()] as SpecRow).filter(([key, value]) => key || value);

/** Вид S → входы поверх prev: опубликованные поля экрана; слоты фото — как в prev (их пишет мост). Пустое поле, которого в prev
    не было, не появляется (как у родной формы: ключ пишет только правка) */
export function inputsOf(form: StudioForm | null | undefined, view: InfogView, prev: Record<string, string>): Record<string, string> {
  const f = infogFields(form);
  const next = { ...prev };
  const put = (field: Field | undefined, value: string | undefined) => {
    if (!field || value === undefined) return;
    if (value === '' && next[field.key] === undefined) return;
    next[field.key] = value;
  };
  if (f.text) put(f.text, view.text);
  else if (f.name) {
    const parts = splitProductText(view.text);
    put(f.name, parts.name);
    put(f.advantages, parts.advantages.join('\n'));
  }
  if (f.style && view.sty && f.style.values.includes(view.sty)) put(f.style, view.sty);
  if (f.platform && view.mp && f.platform.values.includes(view.mp)) put(f.platform, view.mp);
  if (f.slides && view.n !== undefined && f.slides.values.includes(String(view.n))) put(f.slides, String(view.n));
  if (f.cover && view.clean !== undefined) put(f.cover, view.clean ? cleanValue(f.cover.values) : textCoverValue(f.cover.values));
  if (f.specs) put(f.specs, formatSpecs(specRows(view.specs)));
  return next;
}

/** Вход заказа → вид S без формы (заявка до прихода формы, хвост после перезагрузки): поля по допустимым именам ключей (product-model KEYS) */
export function viewOfInputs(form: StudioForm | null | undefined, inputs: Record<string, unknown>): InfogView {
  const strings = Object.fromEntries(Object.entries(inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  if (form) return viewOf(form, strings);
  const first = (keys: string[]) => keys.map(key => strings[key]).find(value => value !== undefined);
  const name = first(['name']);
  const text = first(['text']) ?? (name !== undefined ? joinProductText(name, (first(['advantages']) ?? '').split('\n')) : '');
  const view: InfogView = { text, specs: parseSpecs(first(['specs'])) };
  const sty = first(['style_id', 'style']);
  if (sty) view.sty = sty;
  const mp = first(['platform', 'market']);
  if (mp) view.mp = mp;
  const n = Number(first(['slides', 'slide_count', 'slides_count']));
  if (Number.isSafeInteger(n) && n > 0) view.n = n;
  const cover = first(['clean_cover', 'cover']);
  if (cover !== undefined) view.clean = cover === 'true' || cover === 'clean';
  return view;
}
